import { BaseAddressClient } from "../types/clients";
import { Address, AddressInput, AddressSuggestion, User, ParhatoAddressObject } from "@core/types";
import { convertAddressSuggestion, convertCustomerAddress, convertCustomerUpdateAddressPayload } from "./converters";
import {
  CustomerAddressType,
  CreateCustomerAddressDocument,
  CreateCustomerAddressMutation,
  CreateCustomerAddressMutationVariables,
  DeleteCustomerAddressMutation,
  DeleteCustomerAddressMutationVariables,
  DeleteCustomerAddressDocument,
  UpdateCustomerAddressDocument,
  UpdateCustomerAddressMutation,
  UpdateCustomerAddressMutationVariables,
  CustomerAddressesDocument,
  CustomerAddressesQuery,
} from "../../../../generated/graphql";
import { client } from "../../../../apollo";

class AddressClient extends BaseAddressClient {
  async getAddressSuggestion(
    address: string,
    abortSignal?: AbortSignal,
    searchPrefix?: string
  ): Promise<AddressSuggestion[] | null> {
    if (!address) return null;

    const { data } = await this.instance.get<ParhatoAddressObject[]>(
      `/search-address?search=${searchPrefix} ${address}`,
      {
        signal: abortSignal,
      }
    );

    const filteredAddressByLocality = data.filter((address) => address.locality);

    const res = filteredAddressByLocality.map(convertAddressSuggestion);

    return res;
  }

  async getCustomerAddresses(customerId: User["id"]): Promise<Address[] | null> {
    if (!customerId) return null;

    const { data } = await client.query<CustomerAddressesQuery>({
      query: CustomerAddressesDocument,
    });

    const addresses =
      data?.customerAddresses?.edges.map(({ node }) => {
        if (node.location) {
          const reversedCoordinates: number[] = [...node.location.coordinates].reverse();

          return {
            ...node,
            location: {
              ...node.location,
              coordinates: reversedCoordinates,
            },
          };
        }

        return node;
      }) ?? [];

    return !!addresses.length
      ? addresses.map((address) => convertCustomerAddress(address as CustomerAddressType))
      : null;
  }

  async createCustomerAddress(customerAddress: AddressInput): Promise<Address | null> {
    const { data } = await client.mutate<CreateCustomerAddressMutation, CreateCustomerAddressMutationVariables>({
      mutation: CreateCustomerAddressDocument,
      refetchQueries: [CustomerAddressesDocument],
      variables: {
        address: customerAddress,
      },
    });

    const address = data?.createCustomerAddress.address;

    if (address && address.location && address.location.coordinates) {
      address.location.coordinates = address.location.coordinates.reverse();
    }

    return !!address ? convertCustomerAddress(address as CustomerAddressType) : null;
  }

  async deleteCustomerAddress(customerAddressId: Address["id"]): Promise<Address["id"] | null> {
    const { data } = await client.mutate<DeleteCustomerAddressMutation, DeleteCustomerAddressMutationVariables>({
      mutation: DeleteCustomerAddressDocument,
      refetchQueries: [CustomerAddressesDocument],
      variables: {
        addressId: customerAddressId!,
      },
    });

    const addressId = data?.deleteCustomerAddress.addressId;

    return addressId ?? null;
  }

  async updateCustomerAddress(customerAddress: Address): Promise<Address | null> {
    const addressPayload = convertCustomerUpdateAddressPayload(customerAddress);

    const { data } = await client.mutate<UpdateCustomerAddressMutation, UpdateCustomerAddressMutationVariables>({
      mutation: UpdateCustomerAddressDocument,
      refetchQueries: [CustomerAddressesDocument],
      variables: {
        addressId: customerAddress.id!,
        address: addressPayload,
      },
    });

    const address = data?.updateCustomerAddress.address;

    return !!address ? convertCustomerAddress(address as CustomerAddressType) : null;
  }
}

export default AddressClient;
